import React, { useMemo, useState } from 'react';
import { LayoutList, LayoutGrid } from 'lucide-react';
import { mockKPIs, mockLoanOfficers, LoanOfficer } from './data/mockData';
import { DashboardHeader, FilterState } from './components/DashboardHeader';
import { KPICards } from './components/KPICards';
import { PerformanceTable, SortConfig } from './components/PerformanceTable';
import { LoanOfficerCards } from './components/LoanOfficerCards';
import { CoachingPanel } from './components/CoachingPanel';
type ViewMode = 'table' | 'cards';
export function App() {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [filters, setFilters] = useState<FilterState>({
    dateRange: '30D',
    team: 'All',
    showFlaggedOnly: false
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'pipelineValue',
    direction: 'desc'
  });
  const [selectedLO, setSelectedLO] = useState<LoanOfficer | null>(null);
  // Extract unique teams for filter
  const teams = useMemo(() => {
    const uniqueTeams = new Set(mockLoanOfficers.map(lo => lo.team));
    return Array.from(uniqueTeams);
  }, []);
  // Filter and sort data
  const filteredAndSortedLOs = useMemo(() => {
    let result = [...mockLoanOfficers];
    // Apply Filters
    if (filters.team !== 'All') {
      result = result.filter(lo => lo.team === filters.team);
    }
    if (filters.showFlaggedOnly) {
      result = result.filter(lo => lo.coachingFlags.length > 0);
    }
    // Apply Sort
    result.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [filters, sortConfig]);
  const handleSort = (key: keyof LoanOfficer) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  return <div className="min-h-screen bg-gray-50 font-sans" data-id="element-0">
      <DashboardHeader filters={filters} onFilterChange={setFilters} teams={teams} data-id="element-1" />

      <main className="max-w-[1600px] mx-auto px-6 pb-12" data-id="element-2">
        <KPICards kpis={mockKPIs} data-id="element-3" />

        {/* View Controls */}
        <div className="flex justify-between items-center mb-4 mt-8" data-id="element-4">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-5">
            Loan Officer Performance
          </h2>

          <div className="flex bg-gray-200 p-1 rounded-lg" data-id="element-6">
            <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} title="Table View" data-id="element-7">
              <LayoutList className="w-5 h-5" data-id="element-8" />
            </button>
            <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} title="Card View" data-id="element-9">
              <LayoutGrid className="w-5 h-5" data-id="element-10" />
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        {filteredAndSortedLOs.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-11">
            <p className="text-gray-500" data-id="element-12">
              No Loan Officers match the current filters.
            </p>
            <button onClick={() => setFilters({
          dateRange: '30D',
          team: 'All',
          showFlaggedOnly: false
        })} className="mt-4 text-blue-600 hover:text-blue-800 font-medium" data-id="element-13">
              Clear Filters
            </button>
          </div> : viewMode === 'table' ? <PerformanceTable loanOfficers={filteredAndSortedLOs} sortConfig={sortConfig} onSort={handleSort} onSelectLO={setSelectedLO} dateRange={filters.dateRange} data-id="element-14" /> : <LoanOfficerCards loanOfficers={filteredAndSortedLOs} onSelectLO={setSelectedLO} dateRange={filters.dateRange} data-id="element-15" />}
      </main>

      {/* Slide-out Panel */}
      <CoachingPanel selectedLO={selectedLO} isOpen={selectedLO !== null} onClose={() => setSelectedLO(null)} data-id="element-16" />
    </div>;
}